import { Observable } from 'rxjs';

// WI 34964: the NCR vendor typeahead searches Viewpoint AP vendors in the user's default company.

/** The most vendors one search returns. Kendo and the mobile list both show a short list. */
export const VENDOR_SEARCH_TOP = 25;

/**
 * The vendor group to search for a Viewpoint company, or null when there is no usable company.
 * Viewpoint keys vendors by vendor group, not company. Every company's VendorGroup equals its
 * company number (bHQCO, checked in prod 2026-10-01), which web-warehouse already relies on.
 * VendorGroup is a tinyint, so anything outside 1..255 is not a vendor group.
 */
export function vendorGroupForCompany(
  vpCompanyId: number | null | undefined
): number | null {
  return typeof vpCompanyId === 'number' &&
    Number.isInteger(vpCompanyId) &&
    vpCompanyId > 0 &&
    vpCompanyId <= 255
    ? vpCompanyId
    : null;
}

/**
 * The OData $filter for a vendor search, or null to list the group unfiltered.
 * Matches the name (case-insensitive contains) and, when the term is all digits, the vendor
 * number exactly. The number match is what lets a saved vendor be found again on reopen,
 * because the form re-searches with the saved vendor number.
 */
export function vendorSearchODataFilter(
  query: string | null | undefined
): string | null {
  const term = `${query ?? ''}`.trim();
  if (!term) {
    return null;
  }
  const quoted = term.replace(/'/g, "''");
  const byName = `contains(tolower(Name), tolower('${quoted}'))`;
  return /^\d{1,9}$/.test(term) ? `${byName} or Vendor eq ${term}` : byName;
}

/** Local match for the offline path: the name contains the term, or the vendor number equals it. */
export function vendorMatchesQuery(query: string, data: unknown): boolean {
  const term = `${query ?? ''}`.trim().toLowerCase();
  if (!term) {
    return true;
  }
  if (!data || typeof data !== 'object') {
    return false;
  }
  const vendor = data as { name?: string; vendor?: number };
  return (
    (vendor.name ?? '').toLowerCase().includes(term) ||
    `${vendor.vendor ?? ''}` === term
  );
}

/** The record property a dynamic form names to receive the vendor's contact (WI 34965). */
export const VENDOR_CONTACT_PROPERTY = 'contact';

/**
 * WI 34965: a stored form can only name `property: 'contact'` in `updateOnValueChange`, but the
 * vendor search result carries no contact. Gives each such entry (unless it already has its own
 * calculator) a lookup of the picked vendor's contact; every other entry is returned unchanged.
 */
export function withVendorContactLookup<
  T extends { property?: string; calculatedPropertyValue?: unknown },
>(
  entries: Array<T>,
  lookup: (
    vendorGroup: number | undefined,
    vendor: number | undefined
  ) => Observable<string | null>
): Array<T> {
  // The selected item is a vendor search record. A null result (failed lookup) pushes nothing to
  // the target; an empty string (no contact) clears a stale auto-filled one (Task 35033).
  const contactOf = ((item: { vendor?: number; vendorGroup?: number }) =>
    lookup(item?.vendorGroup, item?.vendor)) as T['calculatedPropertyValue'];
  return entries.map((entry) =>
    entry.property === VENDOR_CONTACT_PROPERTY && !entry.calculatedPropertyValue
      ? { ...entry, calculatedPropertyValue: contactOf }
      : entry
  );
}
